import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export type ToastVariant = 'default' | 'success' | 'danger' | 'warning' | 'info'

export type ShowToastOptions = {
  /** Conteúdo totalmente customizado. Quando informado, ignora `title`/`description`. */
  content?: ReactNode
  title?: string
  description?: ReactNode
  variant?: ToastVariant
  /** Cor de destaque (borda esquerda + ícone padrão), sobrescreve a cor da variante. */
  accentColor?: string
  /** Tempo em ms até fechar sozinho. `null` mantém o toast até ser fechado manualmente. Padrão: 6000. */
  duration?: number | null
  onClick?: () => void
  /** Chamado apenas quando o toast é fechado pelo botão de fechar (X), não no clique no corpo nem no auto-dismiss. */
  onDismiss?: () => void
}

export type ToastItem = ShowToastOptions & { id: string }

type ToastContextType = {
  toasts: ToastItem[]
  showToast: (options: ShowToastOptions) => string
  closeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

const DEFAULT_DURATION = 6000

let toastIdCounter = 0

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const timeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const closeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
    clearTimeout(timeouts.current[id])
    delete timeouts.current[id]
  }, [])

  const showToast = useCallback(
    (options: ShowToastOptions) => {
      const id = `toast-${++toastIdCounter}`
      const duration = options.duration === undefined ? DEFAULT_DURATION : options.duration

      setToasts((prev) => [...prev, { ...options, id }])

      if (duration !== null) {
        timeouts.current[id] = setTimeout(() => closeToast(id), duration)
      }

      return id
    },
    [closeToast]
  )

  useEffect(() => {
    const pendingTimeouts = timeouts.current
    return () => {
      Object.values(pendingTimeouts).forEach(clearTimeout)
    }
  }, [])

  const contextValue = useMemo(
    () => ({ toasts, showToast, closeToast }),
    [toasts, showToast, closeToast]
  )

  return <ToastContext.Provider value={contextValue}>{children}</ToastContext.Provider>
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
